/* eslint-disable react/no-unescaped-entities */
import { Link } from "react-router-dom";
import { ArrowUpRight, Linkedin, Mail, MapPin } from "lucide-react";
import { ASSETS, FOD_URL, LINKEDIN, CONTACT_EMAIL } from "../../lib/assets";

const metiers = [
    { label: "Financement", to: "/financement", external: false },
    { label: "Stratégie financière", to: "/#strategie", external: false },
    { label: "Frais bancaires · FOD", to: FOD_URL, external: true },
];

const cabinetLinks = [
    { label: "Le cabinet", to: "/#cabinet" },
    { label: "Opérations", to: "/#operations" },
    { label: "Médias", to: "/#medias" },
    { label: "Contact", to: "/contact" },
];

export default function Footer() {
    return (
        <footer
            data-testid="footer"
            className="bg-dmb-navy-deep text-white"
            style={{ background: "var(--dmb-navy-deep)" }}
        >
            <div className="container-dmb pt-16 pb-7 flex flex-col gap-11">
                <div className="flex flex-wrap gap-10 lg:gap-14">
                    {/* Brand column */}
                    <div className="flex-1 min-w-[260px] flex flex-col gap-5">
                        <img
                            src={ASSETS.logoWhite}
                            alt="DMB Partners"
                            className="h-6 w-auto"
                        />
                        <p className="text-[14px] leading-[1.65] text-dmb-on-dark max-w-[340px] m-0">
                            Des anciens banquiers, de votre côté de la table.
                            Financement et stratégie financière des PME et ETI.
                        </p>
                        <Link
                            to="/contact"
                            data-testid="footer-cta-contact"
                            className="btn-dmb btn-primary self-start !h-11 !px-5 text-[14px]"
                        >
                            Présenter un projet
                        </Link>
                    </div>

                    {/* Métiers */}
                    <nav
                        aria-label="Métiers"
                        className="flex-1 min-w-[170px] flex flex-col gap-3 text-[14px]"
                    >
                        <span className="text-[12px] font-semibold uppercase tracking-[0.14em] text-dmb-on-dark-2 mb-1">
                            Métiers
                        </span>
                        {metiers.map((l) =>
                            l.external ? (
                                <a
                                    key={l.label}
                                    href={l.to}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    data-testid={`footer-link-${l.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                                    className="text-white hover:text-dmb-green-light transition-colors inline-flex items-center gap-1"
                                >
                                    {l.label}
                                    <ArrowUpRight className="w-3.5 h-3.5" />
                                </a>
                            ) : (
                                <Link
                                    key={l.label}
                                    to={l.to}
                                    data-testid={`footer-link-${l.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                                    className="text-white hover:text-dmb-green-light transition-colors"
                                >
                                    {l.label}
                                </Link>
                            ),
                        )}
                    </nav>

                    {/* Cabinet */}
                    <nav
                        aria-label="Cabinet"
                        className="flex-1 min-w-[170px] flex flex-col gap-3 text-[14px]"
                    >
                        <span className="text-[12px] font-semibold uppercase tracking-[0.14em] text-dmb-on-dark-2 mb-1">
                            Cabinet
                        </span>
                        {cabinetLinks.map((l) => (
                            <Link
                                key={l.label}
                                to={l.to}
                                data-testid={`footer-link-${l.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                                className="text-white hover:text-dmb-green-light transition-colors"
                            >
                                {l.label}
                            </Link>
                        ))}
                    </nav>

                    {/* Contact */}
                    <div className="flex-1 min-w-[220px] flex flex-col gap-3 text-[14px]">
                        <span className="text-[12px] font-semibold uppercase tracking-[0.14em] text-dmb-on-dark-2 mb-1">
                            Contact
                        </span>
                        <a
                            href={`mailto:${CONTACT_EMAIL}`}
                            data-testid="footer-email"
                            className="text-white hover:text-dmb-green-light transition-colors inline-flex items-center gap-2"
                        >
                            <Mail className="w-4 h-4 text-dmb-green-light" />
                            {CONTACT_EMAIL}
                        </a>
                        <span
                            data-testid="footer-address"
                            className="text-white inline-flex items-center gap-2"
                        >
                            <MapPin className="w-4 h-4 text-dmb-green-light" />
                            149 avenue du Maine, 75014 Paris
                        </span>
                        <a
                            href={LINKEDIN.company}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="footer-linkedin"
                            className="text-white hover:text-dmb-green-light transition-colors inline-flex items-center gap-2"
                        >
                            <Linkedin className="w-4 h-4 text-dmb-green-light" />
                            LinkedIn DMB Partners
                            <ArrowUpRight className="w-3.5 h-3.5" />
                        </a>
                    </div>
                </div>

                {/* Mention d'adhésion MEDEF Paris */}
                <div
                    data-testid="footer-medef"
                    className="flex items-center gap-4 pt-6"
                    style={{ borderTop: "1px solid rgba(255,255,255,0.12)" }}
                >
                    <a
                        href="https://www.medefparis.fr/"
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label="MEDEF Paris"
                        data-testid="footer-medef-link"
                        className="shrink-0 inline-flex items-center bg-white hover:opacity-90 transition-opacity"
                        style={{ borderRadius: 8, padding: "8px 12px" }}
                    >
                        <img
                            src="/logos/medef-paris.png"
                            alt="MEDEF Paris"
                            className="h-10 w-auto"
                        />
                    </a>
                    <div>
                        <div className="text-white text-[14px] font-semibold leading-snug">
                            Adhérent du MEDEF Paris
                        </div>
                        <div className="text-dmb-on-dark-2 text-[12px] leading-snug mt-0.5">
                            DMB Partners est adhérent du Mouvement des Entreprises de France Paris
                        </div>
                    </div>
                </div>

                <div className="pt-7 border-t border-white/10 flex flex-col lg:flex-row gap-4 items-start lg:items-center justify-between">
                    <p
                        data-testid="footer-legal-line"
                        className="text-[13px] text-dmb-on-dark-2 m-0"
                    >
                        © 2026 DMB Partners · SAS au capital de 1 000 € · RCS Paris
                        928 053 487 · ORIAS n° 25003576 · Adhérent du MEDEF Paris
                    </p>
                    <ul className="flex flex-wrap gap-x-5 gap-y-2 text-[13px] m-0 p-0 list-none">
                        <li>
                            <Link
                                to="/mentions-legales"
                                data-testid="footer-legal"
                                className="text-dmb-on-dark hover:text-white transition-colors"
                            >
                                Mentions légales
                            </Link>
                        </li>
                        <li>
                            <Link
                                to="/confidentialite"
                                data-testid="footer-privacy"
                                className="text-dmb-on-dark hover:text-white transition-colors"
                            >
                                Confidentialité
                            </Link>
                        </li>
                        <li>
                            <Link
                                to="/cookies"
                                data-testid="footer-cookies"
                                className="text-dmb-on-dark hover:text-white transition-colors"
                            >
                                Cookies
                            </Link>
                        </li>
                    </ul>
                </div>
            </div>
        </footer>
    );
}
